/**
 * MelExpiryWarning Component
 * Warning banner for MELs that are expiring soon or have expired
 */

import React from 'react';
import { cn } from '@/utils/cn';
import { Link } from 'react-router-dom';
import type { ActiveMel } from '@/types/mel';
import melService from '@/services/melService';
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface MelExpiryWarningProps {
  mels: ActiveMel[];
  variant?: 'banner' | 'compact' | 'inline';
  showLink?: boolean;
  className?: string;
}

export const MelExpiryWarning: React.FC<MelExpiryWarningProps> = ({
  mels,
  variant = 'banner',
  showLink = true,
  className,
}) => {
  // Separate expired and expiring soon
  const expiredMels = mels.filter((m) => melService.isExpired(m.expiresAt));
  const expiringSoonMels = mels.filter(
    (m) => melService.isExpiringSoon(m.expiresAt) && !melService.isExpired(m.expiresAt)
  );

  const hasExpired = expiredMels.length > 0;
  const hasExpiringSoon = expiringSoonMels.length > 0;

  if (!hasExpired && !hasExpiringSoon) return null;

  if (variant === 'inline') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 text-sm',
          hasExpired ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400',
          className
        )}
      >
        {hasExpired ? (
          <>
            <XCircleIcon className="w-4 h-4" />
            {expiredMels.length} expired MEL{expiredMels.length !== 1 ? 's' : ''}
          </>
        ) : (
          <>
            <ExclamationTriangleIcon className="w-4 h-4" />
            {expiringSoonMels.length} MEL{expiringSoonMels.length !== 1 ? 's' : ''} expiring soon
          </>
        )}
      </span>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg border',
          hasExpired
            ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
          className
        )}
      >
        {hasExpired ? (
          <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
        ) : (
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm font-medium',
              hasExpired ? 'text-red-800 dark:text-red-300' : 'text-yellow-800 dark:text-yellow-300'
            )}
          >
            {hasExpired
              ? `${expiredMels.length} Expired MEL${expiredMels.length !== 1 ? 's' : ''}`
              : `${expiringSoonMels.length} MEL${expiringSoonMels.length !== 1 ? 's' : ''} Expiring Soon`}
          </p>
        </div>
        {showLink && (
          <Link
            to="/mel/active"
            className={cn(
              'flex-shrink-0 text-sm font-medium',
              hasExpired
                ? 'text-red-700 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                : 'text-yellow-700 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300'
            )}
          >
            View
          </Link>
        )}
      </div>
    );
  }

  // Default banner variant
  return (
    <div
      className={cn(
        'rounded-lg border overflow-hidden',
        hasExpired
          ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
          : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4">
        <div
          className={cn(
            'p-2 rounded-lg',
            hasExpired ? 'bg-red-100 dark:bg-red-900/40' : 'bg-yellow-100 dark:bg-yellow-900/40'
          )}
        >
          {hasExpired ? (
            <XCircleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
          ) : (
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
          )}
        </div>
        <div className="flex-1">
          <h3
            className={cn(
              'font-semibold',
              hasExpired ? 'text-red-800 dark:text-red-300' : 'text-yellow-800 dark:text-yellow-300'
            )}
          >
            {hasExpired ? 'MEL Items Expired' : 'MEL Items Expiring Soon'}
          </h3>
          <p
            className={cn(
              'text-sm',
              hasExpired ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
            )}
          >
            {hasExpired
              ? 'The following MEL items have exceeded their time limits and require immediate attention.'
              : 'The following MEL items will expire within the next 3 days.'}
          </p>
        </div>
      </div>

      {/* MEL List */}
      <div className="px-4 pb-4">
        <div className="space-y-2">
          {(hasExpired ? expiredMels : expiringSoonMels).slice(0, 5).map((mel) => (
            <div
              key={mel.id}
              className={cn(
                'flex items-center justify-between p-3 rounded-lg',
                hasExpired ? 'bg-red-100/50 dark:bg-red-900/30' : 'bg-yellow-100/50 dark:bg-yellow-900/30'
              )}
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-bold text-foreground">
                  {mel.melItem.itemNumber}
                </span>
                <span className="text-sm text-foreground/80">{mel.melItem.title}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground/60">{mel.aircraft.tailNumber}</span>
                <span
                  className={cn(
                    'flex items-center gap-1 font-medium',
                    hasExpired
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-yellow-700 dark:text-yellow-400'
                  )}
                >
                  <ClockIcon className="w-4 h-4" />
                  {melService.formatExpiryStatus(mel.expiresAt)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Show more link */}
        {(hasExpired ? expiredMels : expiringSoonMels).length > 5 && (
          <p
            className={cn(
              'text-sm mt-2',
              hasExpired ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
            )}
          >
            +{(hasExpired ? expiredMels : expiringSoonMels).length - 5} more
          </p>
        )}
      </div>

      {/* Action */}
      {showLink && (
        <div
          className={cn(
            'p-4 border-t',
            hasExpired
              ? 'border-red-200 dark:border-red-800'
              : 'border-yellow-200 dark:border-yellow-800'
          )}
        >
          <Link
            to="/mel/active"
            className={cn(
              'flex items-center justify-center gap-2 w-full py-2 rounded-lg font-medium transition-colors',
              hasExpired
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-yellow-600 hover:bg-yellow-700 text-white'
            )}
          >
            View All Active MELs
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

/**
 * Simple count badge for navigation
 */
export const MelExpiryBadge: React.FC<{
  count: number;
  hasExpired: boolean;
  className?: string;
}> = ({ count, hasExpired, className }) => {
  if (count === 0) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full',
        hasExpired
          ? 'bg-red-500 text-white'
          : 'bg-yellow-500 text-yellow-900',
        className
      )}
    >
      {count}
    </span>
  );
};

export default MelExpiryWarning;
